// Custom Popup Logic - Assets/custom-popup.js

class CustomPopup {
  constructor(popupElement) {
    if (!popupElement) return;
    this.popup = popupElement;
    this.popupId = this.popup.id;
    this.overlay = document.getElementById(`${this.popupId}__overlay`);
    this.closeButtons = this.popup.querySelectorAll(`[data-popup-close][data-popup-target="${this.popupId}"]`);
    this.focusableElements = Array.from(
      this.popup.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    );
    this.firstFocusable = this.focusableElements[0];
    this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);

    this.init();
  }

  init() {
    this.closeButtons.forEach(button => button.addEventListener('click', this.close));
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    // --- Example Trigger Bindings ---
    // You'll call `this.open()` based on your desired triggers elsewhere.
    // See Step 5 for examples.
  }

  open(event) {
    if (event) event.preventDefault(); // Prevent default link/button behavior if triggered by one

    if (!this.popup || !this.overlay) return;

    this.popup.classList.add('is-visible');
    this.popup.setAttribute('aria-hidden', 'false');
    this.overlay.classList.add('is-visible');
    this.overlay.setAttribute('aria-hidden', 'false');

    document.body.style.overflow = 'hidden'; // Prevent background scrolling
    document.addEventListener('keydown', this.handleKeydown);

    // Focus the first focusable element or the popup itself
    if (this.firstFocusable) {
      this.firstFocusable.focus();
    } else {
      this.popup.focus();
    }

    // Optional: Dispatch an event when opened
    this.popup.dispatchEvent(new CustomEvent('popup:opened', { bubbles: true }));
  }

  close() {
    if (!this.popup || !this.overlay) return;

    this.popup.classList.remove('is-visible');
    this.popup.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-visible');
    this.overlay.setAttribute('aria-hidden', 'true');

    document.body.style.overflow = ''; // Restore background scrolling
    document.removeEventListener('keydown', this.handleKeydown);

    // Return focus to the element that opened the popup, if applicable
    if (this.opener) {
      this.opener.focus();
      this.opener = null; // Clear opener reference
    }

    // Optional: Dispatch an event when closed
    this.popup.dispatchEvent(new CustomEvent('popup:closed', { bubbles: true }));
  }

  handleOverlayClick(event) {
     // Only close if the click is directly on the overlay, not on the popup content
     if (event.target === this.overlay) {
        this.close();
     }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    // Focus Trapping
    if (event.key === 'Tab') {
       // If only one or no focusable elements, prevent tabbing
       if (this.focusableElements.length <= 1) {
          event.preventDefault();
          return;
       }
      if (event.shiftKey) { // Shift + Tab
        if (document.activeElement === this.firstFocusable) {
          this.lastFocusable.focus();
          event.preventDefault();
        }
      } else { // Tab
        if (document.activeElement === this.lastFocusable) {
          this.firstFocusable.focus();
          event.preventDefault();
        }
      }
    }
  }

  // Method to set the element that triggered the popup opening
  setOpener(element) {
     this.opener = element;
  }
}

// --- Initialization ---
// Find all popup modals on the page and instantiate the class
// Use a Map to store instances, keyed by popup ID
window.customPopups = window.customPopups || new Map();

function initializeCustomPopups() {
  document.querySelectorAll('[data-popup-modal]').forEach(popupElement => {
    const popupId = popupElement.id;
    if (!window.customPopups.has(popupId)) {
       console.log(`Initializing popup: ${popupId}`); // Debug log
       window.customPopups.set(popupId, new CustomPopup(popupElement));
    }
  });
}

// Initialize on initial load and after Shopify section rendering
document.addEventListener('DOMContentLoaded', initializeCustomPopups);
document.addEventListener('shopify:section:load', initializeCustomPopups); // For theme editor updates

// --- Global function to open a specific popup ---
// You can call this from inline script tags or other JS files
// e.g., openCustomPopup('newsletter-popup', optionalTriggerElement)
function openCustomPopup(popupId, triggerElement = null) {
  const popupInstance = window.customPopups.get(popupId);
  if (popupInstance) {
    console.log(`Opening popup: ${popupId}`); // Debug log
    if (triggerElement) {
       popupInstance.setOpener(triggerElement); // Store the trigger element
    }
    popupInstance.open();
  } else {
    console.warn(`CustomPopup with ID "${popupId}" not found or not initialized.`);
  }
}

// --- Global function to close a specific popup ---
function closeCustomPopup(popupId) {
  const popupInstance = window.customPopups.get(popupId);
  if (popupInstance) {
    console.log(`Closing popup: ${popupId}`); // Debug log
    popupInstance.close();
  } else {
    console.warn(`CustomPopup with ID "${popupId}" not found or not initialized.`);
  }
}